// src/components/cities/FormAddEditCity.jsx

import React, { useEffect, useState } from "react";
import { Box, Paper, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateCity from "../../hooks/cities/useCreateCity";
import useUpdateCity from "../../hooks/cities/useUpdateCity";
import useGetCountriesForSelect from "../../hooks/countries/useGetCountriesForSelect";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditCity = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const initialLang = localStorage.getItem("i18nextLng") || i18n.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [countryId, setCountryId] = useState(currentData?.country_id || "");
    const [nameAr, setNameAr] = useState(currentData?.name?.ar || "");
    const [nameEn, setNameEn] = useState(currentData?.name?.en || "");
    const [status, setStatus] = useState(currentData?.status || "active");

    const { options: countryOptions, isLoading: isLoadingCountries } = useGetCountriesForSelect();

    const { createCityMutate, isPending: isCreating } = useCreateCity();
    const { updateCityMutate, isPending: isUpdating } = useUpdateCity();

    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setCountryId(currentData?.country_id || "");
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setStatus(currentData?.status || "active");
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            country_id: countryId || null,
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            status,
        };

        if (isEdit && currentData?.id) {
            updateCityMutate({ id: currentData.id, req: payload });
        } else {
            createCityMutate(payload);
        }
    };

    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    const countryOptionsTransformed = countryOptions.map((opt) => ({
        id: opt.value,
        name: opt.label,
    }));

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    "& .MuiTextField-root": { m: 1, width: "100%" },
                    "& .MuiFormControl-root": { m: 1, width: "100%" },
                }}
                noValidate
                autoComplete="off"
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الدولة" : "Country"}
                            type="select"
                            name="country_id"
                            value={countryId || ""}
                            onChange={(e) => setCountryId(e.target.value)}
                            options={countryOptionsTransformed}
                            required
                            disabled={isLoadingCountries}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الحالة" : "Status"}
                            type="select"
                            name="status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            options={statusOptions}
                            required
                        />
                    </Grid>

                    {showArabic && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الاسم بالعربية" : "Name in Arabic"}
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {showEnglish && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الاسم بالإنجليزية" : "Name in English"}
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loadingSubmit}
                                sx={{ minWidth: 120 }}
                            >
                                {loadingSubmit
                                    ? (isArabic ? "جاري الحفظ..." : "Saving...")
                                    : isEdit
                                    ? (isArabic ? "تحديث" : "Update")
                                    : (isArabic ? "إنشاء" : "Create")}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
};

export default FormAddEditCity;
